/**
 * AuditLogViewer Component
 *
 * Main component for viewing, filtering, and exporting audit logs
 * Provides administrative interface for investigating changes and generating compliance reports
 *
 * Related Story: WORKFORCE-V2-story-2.004
 * Route: /workforce/audit-logs
 */

import React, { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { EmptyState } from '@/components/ui/EmptyState'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Pagination } from '@/components/ui/Pagination'
import { Alert } from '@/components/ui/alert'
import {
  FileText,
  RefreshCw,
  Download,
  Eye,
  Users,
  Clock,
  Activity,
  Filter,
  Search,
  X
} from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  fetchAuditLogs,
  exportAuditLogsToCsv,
  type AuditLogEntry,
  type AuditLogQueryParams
} from '@/lib/services/audit-log-api'
import { AuditLogFilters } from './AuditLogFilters'
import { AuditLogDetailView } from './AuditLogDetailView'
import { AuditLogStatistics } from './AuditLogStatistics'
import { cn } from '@/lib/utils'

export function AuditLogViewer() {
  // State management
  const [logs, setLogs] = useState<AuditLogEntry[]>([])
  const [filteredLogs, setFilteredLogs] = useState<AuditLogEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null)
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [nextToken, setNextToken] = useState<string | undefined>(undefined)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date())
  const [showFilters, setShowFilters] = useState(true)
  const [showStatistics, setShowStatistics] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  // Filter state
  const [filters, setFilters] = useState<AuditLogQueryParams>({
    limit: 100
  })

  const ITEMS_PER_PAGE = 100

  /**
   * Fetch audit logs from API
   */
  const loadAuditLogs = async (params?: AuditLogQueryParams) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetchAuditLogs({
        ...filters,
        ...params,
        limit: ITEMS_PER_PAGE
      })

      setLogs(response.items)
      setFilteredLogs(response.items) // Initial filtered logs same as all logs
      setNextToken(response.pagination.nextToken)
      setHasNextPage(response.pagination.hasNextPage)
      setLastRefreshTime(new Date())
    } catch (err) {
      console.error('Failed to load audit logs:', err)
      setError(err instanceof Error ? err.message : 'Erro ao carregar logs de auditoria')
      setLogs([])
      setFilteredLogs([])
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Initial load
   */
  useEffect(() => {
    loadAuditLogs()
  }, [])

  /**
   * Apply client-side search filtering
   */
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredLogs(logs)
      return
    }

    const searchLower = searchTerm.toLowerCase()
    const filtered = logs.filter(log =>
      log.entityId.toLowerCase().includes(searchLower) ||
      log.userName.toLowerCase().includes(searchLower) ||
      log.operation.toLowerCase().includes(searchLower)
    )

    setFilteredLogs(filtered)
  }, [searchTerm, logs])

  /**
   * Handle filter changes
   */
  const handleFilterChange = (newFilters: AuditLogQueryParams) => {
    setFilters(newFilters)
    setCurrentPage(1)
    setNextToken(undefined)
    loadAuditLogs(newFilters)
  }

  /**
   * Handle manual refresh
   */
  const handleRefresh = () => {
    setCurrentPage(1)
    setNextToken(undefined)
    loadAuditLogs()
  }

  /**
   * Handle page change
   */
  const handlePageChange = (page: number) => {
    setCurrentPage(page)

    if (page > currentPage && nextToken) {
      // Load next page
      loadAuditLogs({ ...filters, nextToken })
    } else {
      // For previous page, we need to reload (DynamoDB limitation)
      setNextToken(undefined)
      loadAuditLogs(filters)
    }
  }

  /**
   * Handle CSV export
   */
  const handleExport = () => {
    exportAuditLogsToCsv(filteredLogs)
  }

  /**
   * Handle row click to open detail view
   */
  const handleRowClick = (log: AuditLogEntry) => {
    setSelectedLog(log)
    setIsDetailViewOpen(true)
  }

  /**
   * Auto-refresh effect
   */
  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(() => {
      loadAuditLogs()
    }, 30000) // 30 seconds

    return () => clearInterval(interval)
  }, [autoRefresh, filters])

  /**
   * Format timestamp to Brazilian locale
   */
  const formatTimestamp = (timestamp: string) => {
    try {
      return format(new Date(timestamp), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })
    } catch {
      return timestamp
    }
  }

  /**
   * Get entity type badge color
   */
  const getEntityTypeBadgeColor = (entityType: string) => {
    switch (entityType) {
      case 'EMPLOYEE':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/40'
      case 'PAYROLL':
        return 'bg-green-500/20 text-green-400 border-green-500/40'
      case 'DEPARTMENT':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/40'
      case 'ROLE':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/40'
      default:
        return 'bg-brand-gray/20 text-brand-gray border-brand-gray/40'
    }
  }

  /**
   * Get operation icon and color
   */
  const getOperationDisplay = (operation: string) => {
    const operationUpper = operation.toUpperCase()

    if (operationUpper.includes('CREATE')) {
      return { icon: '+', color: 'text-green-400', label: 'Criação' }
    } else if (operationUpper.includes('UPDATE') || operationUpper.includes('CHANGE')) {
      return { icon: '', color: 'text-yellow-400', label: 'Atualização' }
    } else if (operationUpper.includes('DELETE')) {
      return { icon: '', color: 'text-red-400', label: 'Exclusão' }
    } else if (operationUpper.includes('STATUS')) {
      return { icon: '', color: 'text-blue-400', label: 'Mudança de Status' }
    }

    return { icon: '•', color: 'text-brand-gray', label: operation }
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive" className="mb-4">
          <Activity className="h-4 w-4" />
          <div className="ml-2">
            <h4 className="font-semibold">Erro ao Carregar Logs de Auditoria</h4>
            <p className="text-sm mt-1">{error}</p>
            <Button
              onClick={handleRefresh}
              variant="outline"
              size="sm"
              className="mt-2"
            >
              Tentar Novamente
            </Button>
          </div>
        </Alert>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-brand-primary" />
            <h1 className="font-display text-3xl font-bold text-white">
              Logs de Auditoria
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {/* Auto-refresh toggle */}
            <Button
              variant={autoRefresh ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className="gap-2"
            >
              <Clock className="w-4 h-4" />
              {autoRefresh ? 'Auto-atualização Ativa' : 'Auto-atualização Inativa'}
            </Button>

            {/* Manual refresh */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              className="gap-2"
            >
              <RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
              Atualizar
            </Button>

            {/* Export CSV */}
            <Button
              variant="default"
              size="sm"
              onClick={handleExport}
              disabled={filteredLogs.length === 0}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </Button>
          </div>
        </div>

        <p className="text-brand-gray text-sm">
          Visualize e investigue mudanças no sistema. Última atualização:{' '}
          {format(lastRefreshTime, "HH:mm:ss", { locale: ptBR })}
        </p>
      </div>

      {/* Toggle Controls */}
      <div className="flex gap-2 mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="gap-2"
        >
          <Filter className="w-4 h-4" />
          {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowStatistics(!showStatistics)}
          className="gap-2"
        >
          <Activity className="w-4 h-4" />
          {showStatistics ? 'Ocultar Estatísticas' : 'Mostrar Estatísticas'}
        </Button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6">
          <AuditLogFilters
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        </div>
      )}

      {/* Statistics */}
      {showStatistics && (
        <div className="mb-6">
          <AuditLogStatistics logs={filteredLogs} />
        </div>
      )}

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gray" />
            <input
              type="text"
              placeholder="Buscar por ID da entidade, usuário ou operação..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 bg-brand-dark border border-brand-gray/20 rounded-lg text-white placeholder-brand-gray focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-gray hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {searchTerm && (
            <p className="mt-2 text-sm text-brand-gray">
              Mostrando {filteredLogs.length} resultado(s) para "{searchTerm}"
            </p>
          )}
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Registros de Auditoria
            {filteredLogs.length > 0 && (
              <span className="text-sm font-normal text-brand-gray ml-2">
                ({filteredLogs.length} entrada{filteredLogs.length !== 1 ? 's' : ''})
              </span>
            )}
          </CardTitle>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner />
            </div>
          ) : filteredLogs.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="Nenhum log de auditoria encontrado"
              description="Ajuste os filtros ou realize operações para gerar logs"
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-brand-gray/20">
                      <th className="text-left p-3 text-sm font-semibold text-brand-gray">Timestamp</th>
                      <th className="text-left p-3 text-sm font-semibold text-brand-gray">Usuário</th>
                      <th className="text-left p-3 text-sm font-semibold text-brand-gray">Tipo de Entidade</th>
                      <th className="text-left p-3 text-sm font-semibold text-brand-gray">Operação</th>
                      <th className="text-left p-3 text-sm font-semibold text-brand-gray">ID da Entidade</th>
                      <th className="text-left p-3 text-sm font-semibold text-brand-gray">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log) => {
                      const operationDisplay = getOperationDisplay(log.operation)

                      return (
                        <tr
                          key={log.auditId}
                          className="border-b border-brand-gray/10 hover:bg-brand-gray/5 cursor-pointer transition-colors"
                          onClick={() => handleRowClick(log)}
                        >
                          <td className="p-3 text-sm text-white font-mono">
                            {formatTimestamp(log.timestamp)}
                          </td>
                          <td className="p-3 text-sm text-white">
                            {log.userName}
                          </td>
                          <td className="p-3">
                            <Badge className={getEntityTypeBadgeColor(log.entityType)}>
                              {log.entityType}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <span className={cn('text-sm font-medium', operationDisplay.color)}>
                              <span className="mr-1">{operationDisplay.icon}</span>
                              {operationDisplay.label}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-brand-gray font-mono">
                            {log.entityId}
                          </td>
                          <td className="p-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRowClick(log)
                              }}
                              className="gap-1"
                            >
                              <Eye className="w-4 h-4" />
                              Ver Detalhes
                            </Button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-6 flex justify-center">
                <Pagination
                  currentPage={currentPage}
                  totalPages={hasNextPage ? currentPage + 1 : currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail View Modal */}
      {selectedLog && (
        <AuditLogDetailView
          log={selectedLog}
          isOpen={isDetailViewOpen}
          onClose={() => {
            setIsDetailViewOpen(false)
            setSelectedLog(null)
          }}
        />
      )}
    </div>
  )
}
